import { useState, useEffect, useRef } from 'react'
import { useClerk } from '@clerk/react'
import { useAuth } from '../stores/AuthContext'
import { useTheme } from '../stores/ThemeContext'
import { useI18n } from '../stores/I18nContext'
import { useProject } from '../stores/ProjectContext'
import { useCollaboration } from '../stores/CollaborationContext'
import { useAI } from '../stores/AIContext'
import { looksLikeInterchangePlan, interchangeToProject } from '../utils/importPlan'
import { getExamples, getExampleData } from '../data/examples'
import HelpModal from './HelpModal'
import ProjectSettingsModal from './ProjectSettingsModal'
import ProjectsModal from './ProjectsModal'
import ShareModal from './ShareModal'
import PresenceIndicator from './PresenceIndicator'
import UserSettingsModal from './UserSettingsModal'
import './Header.css'

export default function Header() {
  const { theme, setTheme, themes, colorMode, toggleColorMode } = useTheme()
  const { language, setLanguage, languages, t } = useI18n()
  const {
    project,
    isDirty,
    newProject,
    loadProject,
    setIsDirty,
    saveProjectToDatabase,
    fetchUserProjects,
    savedProjects,
    loadProjectFromDatabase,
    currentProjectId,
    projectRole,
    saveStatus,
    saveError
  } = useProject()
  const { togglePanel, isOpen: aiPanelOpen } = useAI()
  const { user } = useAuth()
  const { signOut } = useClerk()
  const [menuOpen, setMenuOpen] = useState(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [projectsOpen, setProjectsOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [userSettingsOpen, setUserSettingsOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const gearMenuRef = useRef(null)
  const fileInputRef = useRef(null)
  const userMenuRef = useRef(null)
  const examples = getExamples(language)

  const toggleMenu = (menu) => {
    setMenuOpen(menuOpen === menu ? null : menu)
  }

  // Update document title when project name changes
  useEffect(() => {
    document.title = `Teddo — ${project.name}`
  }, [project.name])

  // MobileTabBar opens the mobile menu by dispatching this event.
  useEffect(() => {
    const open = () => setMobileMenuOpen(true)
    window.addEventListener('teddo:open-mobile-menu', open)
    return () => window.removeEventListener('teddo:open-mobile-menu', open)
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          gearMenuRef.current && !gearMenuRef.current.contains(event.target)) {
        setMenuOpen(null)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }

    if (menuOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen, userMenuOpen])

  // Avatar helpers
  const getUserInitials = () => {
    if (!user) return '?'
    if (user.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }
    return user.email?.[0]?.toUpperCase() || '?'
  }

  const getUserColor = () => {
    const str = user?.email || user?.name || ''
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = ((hash % 360) + 360) % 360
    return `hsl(${hue}, 45%, 45%)`
  }

  // Export project as JSON
  const handleExport = () => {
    const dataStr = JSON.stringify(project, null, 2)
    const blob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${project.name.replace(/\s+/g, '-').toLowerCase()}.json`
    a.click()
    URL.revokeObjectURL(url)
    setMenuOpen(null)
  }

  // Import project from JSON
  const handleImport = () => {
    fileInputRef.current?.click()
    setMenuOpen(null)
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result)
        // Accept both Teddo's native export and the AI "interchange" plan
        // format (from the downloadable skill) — convert the latter first so
        // it imports deterministically, without an AI round-trip.
        const project = looksLikeInterchangePlan(data) ? interchangeToProject(data) : data
        loadProject(project)
      } catch (err) {
        alert('Invalid JSON file')
      }
    }
    reader.readAsText(file)
    e.target.value = '' // Reset input
  }

  // Save to database
  const handleSave = async () => {
    setSaving(true)
    const result = await saveProjectToDatabase()
    setSaving(false)
    if (result.success) {
      // Success message handled by context
    } else {
      alert(`Failed to save: ${result.error}`)
    }
    setMenuOpen(null)
  }

  // Open projects list modal
  const handleOpen = async () => {
    await fetchUserProjects()
    setProjectsOpen(true)
    setMenuOpen(null)
  }

  // Load example template
  const handleLoadExample = (exampleId) => {
    const exampleData = getExampleData(exampleId, language)
    if (exampleData) {
      loadProject(exampleData)
    }
    setMenuOpen(null)
  }

  return (
    <>
      <header className="header">
        <div className="header-left">
          {/* Mobile menu button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>

          <div className="logo" onClick={() => setHelpOpen(true)} title="Click for help">
            <img src="/teddo_the_icon_with_transparent.png" alt="TeDdO" className="logo-icon-img" />
            <span className="logo-text">
              <span className="logo-cap">T</span>e<span className="logo-cap">D</span>d<span className="logo-cap">O</span>
            </span>
          </div>

          <nav className="nav-menu" ref={menuRef}>
            <div className="menu-item">
              <button onClick={() => toggleMenu('file')} className="menu-button">
                {t('menu.file')}
              </button>
              {menuOpen === 'file' && (
                <div className="dropdown">
                  <button onClick={() => { newProject(); setMenuOpen(null); }}>
                    {t('menu.new')}
                  </button>
                  <button onClick={handleOpen}>{t('menu.open')}</button>
                  <button onClick={handleSave}>{t('menu.save')}</button>
                  <hr />
                  <div className="dropdown-section">
                    <label>{t('templates.startFromTemplate')}</label>
                    {examples.map((example) => (
                      <button key={example.id} onClick={() => handleLoadExample(example.id)}>
                        {example.icon} {example.name}
                      </button>
                    ))}
                  </div>
                  <hr />
                  <button onClick={handleExport}>{t('menu.export')}</button>
                  <button onClick={handleImport}>{t('menu.import')}</button>
                </div>
              )}
            </div>
          </nav>
        </div>

        <div className="header-center">
          <h1 className="project-name">
            {project.name}
            {isDirty && !saveStatus && <span className="unsaved-indicator">•</span>}
          </h1>
          <PresenceIndicator />
        </div>

        <div className="header-right">
          <span
            className={`status-text ${saveStatus} ${isDirty && !saveStatus ? 'unsaved' : ''}`}
            title={saveError || ''}
          >
            {saveStatus === 'saving' && <><span className="status-icon">🍃</span> Saving...</>}
            {saveStatus === 'saved' && !isDirty && <><span className="status-icon">🌿</span> Safe</>}
            {saveStatus === 'error' && <><span className="status-icon">🥀</span> {saveError ? 'Cannot save' : 'Error saving'}</>}
            {!saveStatus && isDirty && currentProjectId && <><span className="status-icon">🌱</span> {t('status.unsaved')}</>}
            {!saveStatus && isDirty && !currentProjectId && <><span className="status-icon">🌱</span> {t('status.unsaved')}</>}
            {!saveStatus && !isDirty && currentProjectId && <><span className="status-icon">🌿</span> {t('status.saved')}</>}
          </span>
          {currentProjectId && projectRole === 'OWNER' && (
            <button
              className="share-btn"
              onClick={() => setShareOpen(true)}
              title="Share project"
            >
              👥
            </button>
          )}
          <button
            className={`ai-btn ${aiPanelOpen ? 'active' : ''}`}
            onClick={togglePanel}
            title={aiPanelOpen ? 'Close TeDdO Assistant' : 'Ask TeDdO'}
          >
            <img src="/teddo_the_icon_with_transparent.png" alt="TeDdO" className="ai-btn-icon" />
          </button>
          <div className="gear-menu" ref={gearMenuRef}>
            <button
              className="settings-btn"
              onClick={() => toggleMenu('gear')}
              title={t('menu.settings')}
            >
              ⚙️
            </button>
            {menuOpen === 'gear' && (
              <div className="dropdown dropdown-right">
                <div className="dropdown-section">
                  <label>{t('menu.language')}</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    {Object.entries(languages).map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="dropdown-section">
                  <label>{t('menu.theme')}</label>
                  <select
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                  >
                    <option value={themes.ghibli}>Studio Ghibli</option>
                    <option value={themes.corporate}>Corporate</option>
                  </select>
                </div>
                <div className="dropdown-section">
                  <div className="color-mode-toggle">
                    <span className="toggle-label">{colorMode === 'light' ? '☀️ Light' : '🌙 Dark'}</span>
                    <button
                      className={`toggle-switch ${colorMode === 'light' ? 'active' : ''}`}
                      onClick={toggleColorMode}
                      aria-label="Toggle light/dark mode"
                    />
                  </div>
                </div>
                <hr />
                <button onClick={() => { setSettingsOpen(true); setMenuOpen(null); }}>
                  {t('menu.projectSettings')}
                </button>
                <button onClick={() => { setUserSettingsOpen(true); setMenuOpen(null); }}>
                  {t('menu.userSettings')}
                </button>
              </div>
            )}
          </div>
          <div className="user-menu" ref={userMenuRef}>
            <button
              className="user-avatar-btn"
              style={{ backgroundColor: getUserColor() }}
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              title={user?.name || user?.email || 'Account'}
            >
              {getUserInitials()}
            </button>
            {userMenuOpen && (
              <div className="user-dropdown">
                <div className="user-dropdown-info">
                  <span className="user-dropdown-name">{user?.name}</span>
                  <span className="user-dropdown-email">{user?.email}</span>
                </div>
                <hr />
                <button onClick={() => { setUserMenuOpen(false); signOut({ redirectUrl: '/' }); }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Hidden file input for import */}
        <input
          type="file"
          ref={fileInputRef}
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
            <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-menu-section">
                <h3>{language === 'nb' ? 'Naviger' : 'Navigate'}</h3>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    window.dispatchEvent(new CustomEvent('teddo:open-flows-drawer'))
                  }}
                >
                  {language === 'nb' ? '📋 Flyt-liste' : '📋 Flow list'}
                </button>
              </div>

              <div className="mobile-menu-section">
                <h3>{t('menu.file')}</h3>
                <button onClick={() => { newProject(); setMobileMenuOpen(false); }}>
                  {t('menu.new')}
                </button>
                <button onClick={() => { handleOpen(); setMobileMenuOpen(false); }}>
                  {t('menu.open')}
                </button>
                <button onClick={() => { handleSave(); setMobileMenuOpen(false); }} disabled={saving}>
                  {saving ? 'Saving...' : t('menu.save')}
                </button>
                <button onClick={() => { handleExport(); setMobileMenuOpen(false); }}>
                  {t('menu.export')}
                </button>
                <button onClick={() => { handleImport(); setMobileMenuOpen(false); }}>
                  {t('menu.import')}
                </button>
              </div>

              <div className="mobile-menu-section">
                <h3>{t('templates.title')}</h3>
                {examples.map((example) => (
                  <button key={example.id} onClick={() => { handleLoadExample(example.id); setMobileMenuOpen(false); }}>
                    {example.icon} {example.name}
                  </button>
                ))}
              </div>

              <div className="mobile-menu-section">
                <h3>{t('menu.settings')}</h3>
                <div className="mobile-menu-setting">
                  <label>{t('menu.language')}</label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    {Object.entries(languages).map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="mobile-menu-setting">
                  <label>{t('menu.theme')}</label>
                  <select
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                  >
                    <option value={themes.ghibli}>Studio Ghibli</option>
                    <option value={themes.corporate}>Corporate</option>
                  </select>
                </div>
                <div className="mobile-menu-setting">
                  <div className="color-mode-toggle">
                    <span className="toggle-label">{colorMode === 'light' ? '☀️ Light' : '🌙 Dark'}</span>
                    <button
                      className={`toggle-switch ${colorMode === 'light' ? 'active' : ''}`}
                      onClick={toggleColorMode}
                      aria-label="Toggle light/dark mode"
                    />
                  </div>
                </div>
                <button onClick={() => { setSettingsOpen(true); setMobileMenuOpen(false); }}>
                  {t('menu.projectSettings')}
                </button>
                <button onClick={() => { setUserSettingsOpen(true); setMobileMenuOpen(false); }}>
                  {t('menu.userSettings')}
                </button>
              </div>

              <div className="mobile-menu-section">
                <h3>Account</h3>
                <div className="mobile-user-info">
                  <div
                    className="user-avatar-btn"
                    style={{ backgroundColor: getUserColor() }}
                  >
                    {getUserInitials()}
                  </div>
                  <span>{user?.name || user?.email}</span>
                </div>
                <button onClick={() => { setMobileMenuOpen(false); signOut({ redirectUrl: '/' }); }}>
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
      <ProjectSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <ProjectsModal isOpen={projectsOpen} onClose={() => setProjectsOpen(false)} />
      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        projectId={currentProjectId}
        projectName={project.name}
        isOwner={projectRole === 'OWNER'}
      />
      <UserSettingsModal isOpen={userSettingsOpen} onClose={() => setUserSettingsOpen(false)} />
    </>
  )
}
